import React, { useState } from "react";
import { useSelector } from "react-redux";
import type { EnvironmentBannerProps } from "./types";
import { Banner, BannerText, Content, Dismiss } from "./styled-components";
import { useBannerInfo, useDevStack } from "./hooks";
import { EnvironmentModal } from "./modal";
import { getIsEnvBannerEnabled } from "./redux/selectors";

const EnvironmentBannerContent = ({ fixed }: EnvironmentBannerProps) => {
  const isEnvBannerEnabled = useSelector(getIsEnvBannerEnabled);
  const { env, hideBanner, isLocal, canChangeEnv, brand } =
    useBannerInfo(isEnvBannerEnabled);
  const devstackName = useDevStack(isEnvBannerEnabled);

  // Deliberately not persisted: dismissing hides the banner for as long as the
  // page lives, and a reload brings it back. A remembered choice is a devstack
  // someone forgets they are on.
  const [isDismissed, setIsDismissed] = useState(false);
  // Local rather than redux: HDR has no modal slice to dispatch into.
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (hideBanner || isDismissed) {
    return null;
  }

  const envUppercased = String(env).toUpperCase();
  const showDevStack = !isLocal && devstackName;

  return (
    <>
      <EnvironmentModal
        env={env}
        brand={brand}
        currentDevstackName={devstackName}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      <Banner
        fixed={fixed}
        environment={env}
        canChangeEnv={canChangeEnv}
        onClick={() => {
          if (canChangeEnv) setIsModalOpen(true);
        }}
        data-qa-label="environmentBanner"
      >
        <Content>
          {showDevStack ? (
            <>
              <BannerText>
                <b>{envUppercased}</b>
              </BannerText>
              <BannerText hasDivisor>
                <b>{devstackName.toUpperCase()}</b>
              </BannerText>
              {brand && (
                <BannerText hasDivisor>
                  <b>{brand.toUpperCase()}</b>
                </BannerText>
              )}
            </>
          ) : (
            <BannerText>
              You&apos;re in <b>{envUppercased}</b>
            </BannerText>
          )}
        </Content>
        <Dismiss
          type="button"
          aria-label="Hide environment banner"
          onClick={(event) => {
            event.stopPropagation();
            setIsDismissed(true);
          }}
        >
          ✕
        </Dismiss>
      </Banner>
    </>
  );
};

const EnvironmentBanner = ({ fixed }: EnvironmentBannerProps) => {
  const isEnvBannerEnabled = useSelector(getIsEnvBannerEnabled);

  if (!isEnvBannerEnabled) {
    return null;
  }

  return <EnvironmentBannerContent fixed={fixed} />;
};

export default EnvironmentBanner;
